/* eslint-disable max-lines */
import dayjs from 'dayjs';

import { Appointment, ComputeAppointmentStatusParams, CreateAppointmentParams, Extension, FormattedAppointment, FormattedExtension, FormattedParticipant, Participant, SupportingInformation } from './appointment.types';
import { formatLocation } from 'FRONTEND/utils/backendApi/locationApi';
import {
  APPOINTMENT_STATUS_BOOKED,
  APPOINTMENT_STATUS_CANCELLED,
  APPOINTMENT_STATUS_PENDING,
  APPOINTMENT_STATUS_PROPOSED,
  APPOINTMENT_STATUS_AWAITING_PAYMENT,
  APPOINTMENT_STATUS_AWAITING_CAPTURE,
  PARTICIPANT_STATUS_ACCEPTED,
  PARTICIPANT_STATUS_NEEDS_ACTION,
  PARTICIPANT_STATUS_TENTATIVE,
  AppointmentScenarios,
  PARTICIPANT_STATUS_DECLINED,
  VIDEO_SPECIALIST_ASSESSMENT,
  OTHER,
} from 'FRONTEND/utils/constants';

export const createExtension = (
  key: string,
  valueType: 'valueString' | 'valueBoolean' | 'valueInteger' | 'valueId',
  value: string | boolean | number,
): Extension => {
  return {
    id: key,
    [valueType]: value,
  };
};

export const createParticipant = (
  id: string,
  type: 'Practitioner' | 'Patient' | 'MOBL' | 'Location' | 'COMM',
  required: 'required' | 'optional' | 'information-only',
  status: 'accepted' | 'declined' | 'tentative' | 'needs-action', // PARTICIPANT_STATUS_NEEDS_ACTION, PARTICIPANT_STATUS_ACCEPTED, PARTICIPANT_STATUS_DECLINED, PARTICIPANT_STATUS_TENTATIVE,
  extensions?: Extension[],
  telecom?: { system: 'email', value: string; use: 'work' }[],
): Participant => {
  return {
    actor: {
      id,
      type,
      telecom: telecom || [],
    },
    required,
    status,
    extension: extensions || [],
  };
};

const createSupportingInformation = (
  id: string,
  type: 'Schedule', // Add other types as needed
): SupportingInformation => {
  return {
    id,
    type,
  };
};
const formatExtension = (rawExtension: Extension): FormattedExtension => {
  return {
    id: rawExtension.id,
    value: rawExtension.valueString ?? rawExtension.valueBoolean ?? rawExtension.valueInteger ?? rawExtension.valueId ?? '',
  };
};

const formatParticipant = (rawParticipant: Participant): FormattedParticipant => {
  return {
    display: rawParticipant.actor?.extension?.find(({ id }) => id === 'Display')?.valueString,
    id: rawParticipant.actor?.id,
    type: rawParticipant.actor?.type,
    required: rawParticipant.required,
    status: rawParticipant.status,
    extension: rawParticipant.extension.map(formatExtension as (ext: Extension) => FormattedExtension) || [],
    email: rawParticipant.actor?.telecom?.find((telecom) => telecom.system === 'email')?.value || '',
    phoneNumber: rawParticipant.actor?.telecom?.find((telecom) => telecom.system === 'phone')?.value || '',
  };
};

export const formatAppointment = (rawAppointment): FormattedAppointment => {
  rawAppointment = rawAppointment.resource as Appointment || rawAppointment as Appointment;
  const patient = rawAppointment.participant.find(
    (participant) => participant.actor.resourceType === 'Patient',
  );
  const secretary = rawAppointment.participant.find(
    (participant) =>
      participant.actor.resourceType === 'Practitioner' &&
      participant.extension.find(({ id }) => id === 'IsSecretary')?.valueBoolean === true,
  );
  const rawRequiredList = rawAppointment.participant.filter(
    (participant) => {
      // If a secretary is present, the only Practioner with isRequester == true is the chosen "Main expert"
      // But in the context of an appointment created by secretary, we still want him to be part of the required list.
      const shouldIncludeMainExpert = secretary ? true : participant.extension.find(({ id }) => id === 'IsRequester')?.valueBoolean === false;
      return participant.actor.resourceType === 'Practitioner' && shouldIncludeMainExpert;
    },
  );
  // In that case of a secretary, we don't display a requester. Otherwise, the requester is the practitioner with isRequester = true
  const rawRequester = secretary ? null : rawAppointment.participant.find(
    (participant) =>
      participant.actor.resourceType === 'Practitioner' &&
      participant.extension.find(({ id }) => id === 'IsRequester')?.valueBoolean === true,
  );
  const location = rawAppointment.participant.find(
    (participant) =>
      participant.actor.type === 'COMM' &&
      participant.actor.resourceType === 'Location',
  )?.actor;
  // Not used at the moment, but could be in the future. Most info already contained in "location", but this contains the display name of the station.
  const station = rawAppointment.participant.find(
    (participant) =>
      participant.actor.type === 'MOBL' &&
      participant.actor.resourceType === 'Location',
  )?.actor;

  const formattedAppointment = {
    ...rawAppointment,
    appointmentId: rawAppointment.id,
    appointmentType: rawAppointment.extension?.find(
      (extension) => extension.id === 'AppointmentType',
    )?.valueString,
    roomId: rawAppointment.extension?.find(
      (extension) => extension.id === 'VideoconferenceRoom',
    )?.valueId,
    startDate: rawAppointment.start,
    endDate: rawAppointment.end,
    reason: rawAppointment.description,
    location: location ? formatLocation(location) : null,
    patient: patient ? {
      ...formatParticipant(patient as Participant),
      userId: patient?.extension?.find(({ id }) => id === 'UserId')?.valueId,
      isRequester: patient?.extension.find(({ id }) => id === 'isRequester')?.value,
      tenantName: patient?.extension?.find((extension) => extension.id === 'tenantName')?.valueString,
      reference: patient?.actor?.reference?.[0],
    } : null,
    requester: secretary ? formatParticipant(secretary) : (rawRequester ? formatParticipant(rawRequester) : null),
    secretary: secretary ? formatParticipant(secretary) : null,
    station: station ? formatParticipant(station) : null,
    requiredMap: rawRequiredList.length ? rawRequiredList.reduce(
      (requiredMap, rawRequired) => {
        // Filter secretary out of requiredMap
        if (rawRequired.actor.id !== secretary?.actor.id) {
          requiredMap[rawRequired.actor.id] = formatParticipant(rawRequired);
        }
        return requiredMap;
      },
      {},
    ) : null,
  };
  // Add canCancelList after initial format.
  formattedAppointment.canCancelList = computeCancelList(formattedAppointment);

  return formattedAppointment as FormattedAppointment;
};

export const computeAppointmentStatus = ({
  participantList,
  isForceBooked = false,
  isCancelled = false,
  isAppointmentWaitingPayment = false,
  isAppointmentWaitingCapture = false,
}: ComputeAppointmentStatusParams) => {
  const participantListWithoutPatient = participantList.filter(
    (participant) => participant.actor.type !== 'Patient',
  );

  const isAppointmentPending = participantListWithoutPatient.some(
    (participant) =>
      [
        PARTICIPANT_STATUS_TENTATIVE,
        PARTICIPANT_STATUS_NEEDS_ACTION,
        PARTICIPANT_STATUS_ACCEPTED,
      ].includes(participant.status),
  );

  const isAppointmentBooked =
    isForceBooked ||
    participantListWithoutPatient.every(
      (participant) =>
        ![
          PARTICIPANT_STATUS_TENTATIVE,
          PARTICIPANT_STATUS_NEEDS_ACTION,
        ].includes(participant.status),
    );

  // Ordered based on priority
  if (isCancelled) {
    return APPOINTMENT_STATUS_CANCELLED;
  }
  if (isAppointmentWaitingCapture) {
    return APPOINTMENT_STATUS_AWAITING_CAPTURE;
  }
  if (isAppointmentWaitingPayment) {
    return APPOINTMENT_STATUS_AWAITING_PAYMENT;
  }
  if (isAppointmentBooked) {
    return APPOINTMENT_STATUS_BOOKED;
  }
  if (isAppointmentPending) {
    return APPOINTMENT_STATUS_PENDING;
  }
  return APPOINTMENT_STATUS_PROPOSED;
};

const computeCurrentScenario = (params: CreateAppointmentParams) => {
  // Logic to determine the current scenario based on args
  // For example, if args contain a specific flag or type, return the corresponding scenario
  const isTelestaffType = params.appointmentType === VIDEO_SPECIALIST_ASSESSMENT;
  const isOtherAppointmentType = params.appointmentType === OTHER;
  const isCreatedAsPatient = params.hasAppointmentsCreateAsPatientRight;
  const hasLocation = !!params.resourceId;
  const isDelegated = params.creatorId !== params.requesterId;

  // Functionnally, assisted works exactly like delegated, it's just consumed differently. It depends on the client/context. (Assisted is consumed by MC, delegated by PC).
  // We don't have a way to distinguish these 2 scenarios, but we also don't really need to right now.
  // Keeping it as a placeholder for future use if needed.
  const isAssisted = false;

  // Patient Portal Scenarios
  if (isCreatedAsPatient && hasLocation) {
    return AppointmentScenarios.PATIENT_PORTAL_BOOTH_TELECONSULTATION;
  } else if (isCreatedAsPatient) {
    return AppointmentScenarios.PATIENT_PORTAL_TELECONSULTATION;
  }

  // Telestaff Scenarios
  if (isDelegated && isTelestaffType) {
    return AppointmentScenarios.DELEGATED_TELESTAFF;
  } else if (isTelestaffType) {
    return AppointmentScenarios.CLASSIC_TELESTAFF;
  }

  // In-Person Consultation
  if (isOtherAppointmentType) {
    return AppointmentScenarios.IN_PERSON_CONSULTATION;
  }

  // Assisted Teleconsultation (placeholder) - Not currently used
  if (isAssisted) {
    return AppointmentScenarios.ASSISTED_TELECONSULTATION;
  }

  // Classic Scenarios
  if (isDelegated) {
    return AppointmentScenarios.DELEGATED_CONSULTATION;
  } else if (hasLocation) {
    return AppointmentScenarios.BOOTH_TELECONSULTATION;
  } else {
    return AppointmentScenarios.CLASSIC_TELECONSULTATION;
  }
};

export const computeCancelList = (appointment: FormattedAppointment) => {
  if (appointment.status === APPOINTMENT_STATUS_CANCELLED) {
    return [];
  }

  // Set avoids duplicates when Prac is requester and required.
  const cancelSet = new Set([
    appointment.secretary?.id,
    ...Object.values(appointment.requiredMap || {}).map((participant) => participant.id),
    appointment.requester?.id,
    appointment.patient?.userId,
  ].filter(Boolean));

  return Array.from(cancelSet); // Needs to be an array so it's serializable.
};

export const createAppointmentBody = (params: CreateAppointmentParams) => {
  const currentScenario = computeCurrentScenario(params);
  const globalExtensions: Extension[] = [];
  const participants: Participant[] = [];

  // Common among all scenarios
  const supportingInformation = [];
  if (params.availabilityId) {
    supportingInformation.push(createSupportingInformation(params.availabilityId, 'Schedule'));
  }

  // Tenant Extensions - Added to globals.
  // `?.` only short-circuits on null/undefined, not on `false`. Callers must pass an array or undefined/null — never a boolean.
  const tenantExtensions = params.tenantIds?.map((tenantId) => createExtension('Tenant', 'valueString', tenantId));

  // Common extensions between scenarios
  const payeeId = params.hasPayment && !params.consultationCodeId && params.requestedIdList.length > 0 ? params.requestedIdList[0] : null;
  globalExtensions.push(
    createExtension('RequesterId', 'valueId', params.requesterId), // NEW
    createExtension('CreatorId', 'valueId', params.creatorId), // NEW
    createExtension('AppointmentType', 'valueString', params.appointmentType),
    ...(tenantExtensions || []),
    payeeId && createExtension('PayeeId', 'valueString', payeeId),
    params.consultationCodeId && createExtension('ConsultationCode', 'valueInteger', params.consultationCodeId),
  );

  // Mainly participant handling.
  switch (currentScenario) {
    case AppointmentScenarios.CLASSIC_TELECONSULTATION:
      participants.push(
        createParticipant(params.requesterId, 'Practitioner', 'required', PARTICIPANT_STATUS_ACCEPTED),
        createParticipant(params.patientId, 'Patient', 'required', PARTICIPANT_STATUS_TENTATIVE),
      );
      break;
    case AppointmentScenarios.ASSISTED_TELECONSULTATION:
    case AppointmentScenarios.DELEGATED_CONSULTATION: {
      const secretary = createParticipant(params.creatorId, 'Practitioner', 'information-only', PARTICIPANT_STATUS_DECLINED);
      secretary.extension.push(createExtension('IsSecretary', 'valueBoolean', true));

      participants.push(
        secretary,
        createParticipant(params.patientId, 'Patient', 'required', PARTICIPANT_STATUS_TENTATIVE),
        createParticipant(params.requesterId, 'Practitioner', 'required', PARTICIPANT_STATUS_ACCEPTED),
        params.resourceId && createParticipant(params.resourceId, 'MOBL', 'required', PARTICIPANT_STATUS_ACCEPTED), // Delegated with booth, maybe merits a separate scenario?
      );
      break;
    }
    case AppointmentScenarios.BOOTH_TELECONSULTATION: {
      // Exclusively non-delegated booth teleconsultation
      participants.push(
        createParticipant(params.requesterId, 'Practitioner', 'required', PARTICIPANT_STATUS_ACCEPTED),
        createParticipant(params.patientId, 'Patient', 'required', PARTICIPANT_STATUS_TENTATIVE),
        createParticipant(params.resourceId, 'MOBL', 'required', PARTICIPANT_STATUS_ACCEPTED),
      );
      break;
    }
    case AppointmentScenarios.PATIENT_PORTAL_TELECONSULTATION:
    case AppointmentScenarios.PATIENT_PORTAL_BOOTH_TELECONSULTATION: {
      const isBooth = currentScenario === AppointmentScenarios.PATIENT_PORTAL_BOOTH_TELECONSULTATION;

      const patientUser = createParticipant(params.requesterId, 'Patient', 'required', PARTICIPANT_STATUS_TENTATIVE);
      patientUser.extension.push(createExtension('IsUser', 'valueBoolean', true));

      participants.push(
        patientUser,
        createParticipant(params.requestedIdList[0], 'Practitioner', 'required', PARTICIPANT_STATUS_ACCEPTED),
        isBooth && createParticipant(params.resourceId, 'MOBL', 'required', PARTICIPANT_STATUS_ACCEPTED), // Booth only
      );
      break;
    }
    case AppointmentScenarios.CLASSIC_TELESTAFF: {
      participants.push(
        createParticipant(params.requesterId, 'Practitioner', 'required', PARTICIPANT_STATUS_ACCEPTED),
        createParticipant(params.patientId, 'Patient', 'information-only', PARTICIPANT_STATUS_DECLINED),
      );
      params.requestedIdList.forEach((additionalPractitionerId) => {
        participants.push(
          createParticipant(additionalPractitionerId, 'Practitioner', 'required', PARTICIPANT_STATUS_ACCEPTED),
        );
      });
      break;
    }
    case AppointmentScenarios.DELEGATED_TELESTAFF: {
      const secretary = createParticipant(params.creatorId, 'Practitioner', 'information-only', PARTICIPANT_STATUS_DECLINED);
      secretary.extension.push(createExtension('IsSecretary', 'valueBoolean', true));
      participants.push(
        secretary,
        createParticipant(params.patientId, 'Patient', 'information-only', PARTICIPANT_STATUS_DECLINED),
      );
      participants.push(
        createParticipant(params.requesterId, 'Practitioner', 'required', PARTICIPANT_STATUS_ACCEPTED),
      );
      params.requestedIdList.forEach((additionalPractitionerId) => {
        participants.push(
          createParticipant(additionalPractitionerId, 'Practitioner', 'required', PARTICIPANT_STATUS_ACCEPTED),
        );
      });
      break;
    }
    case AppointmentScenarios.IN_PERSON_CONSULTATION: {
      // This is a weird one, same as DELEGATED_CONSULTATION but patient is information-only and declined
      const isDelegated = params.creatorId !== params.requesterId;
      let secretary: Participant | null = null;
      if (isDelegated) {
        secretary = createParticipant(params.creatorId, 'Practitioner', 'information-only', PARTICIPANT_STATUS_DECLINED);
        secretary.extension.push(createExtension('IsSecretary', 'valueBoolean', true));
      }

      participants.push(
        isDelegated && secretary,
        createParticipant(params.patientId, 'Patient', 'information-only', PARTICIPANT_STATUS_DECLINED),
        createParticipant(params.requestedIdList[0], 'Practitioner', 'required', PARTICIPANT_STATUS_ACCEPTED),
      );
      break;
    }
  }

  // Add third party participants
  params?.appointmentInfo?.thirdPartyEmailList?.forEach((thirdPartyEmail) => {
    participants.push(
      createParticipant('', 'Practitioner', 'optional', PARTICIPANT_STATUS_TENTATIVE, [], [{ system: 'email', value: thirdPartyEmail, use: 'work' }]));
  });

  // Retro-compatibility for old versions of Medcapture
  // Add isRequester to all participants
  // This can be deprecated if those versions of MC are no longer supported
  const cleanParticipants = participants.filter(Boolean);
  cleanParticipants.forEach((participant) => {
    const isRequester = participant.actor.id === params.requesterId;
    participant.extension.push(createExtension('IsRequester', 'valueBoolean', isRequester));
  });

  return {
    resourceType: 'Appointment',
    status: computeAppointmentStatus({ participantList: cleanParticipants, isForceBooked: false }),
    description: params.appointmentInfo.reason,
    start: dayjs(params.startDate).toISOString(),
    end: dayjs(params.endDate).toISOString(),
    participant: cleanParticipants,
    supportingInformation: supportingInformation.filter(Boolean),
    extension: globalExtensions.filter(Boolean),
    unrestricted: params.isUnrestricted || false,
  };
};

export const getAppointmentExpertNames = (appointment: FormattedAppointment) => {
  const names = [];
  const isPatientRequestedAppointment = appointment.patient.isRequester;
  if (!isPatientRequestedAppointment && appointment.requester?.display) {
    names.push(appointment.requester.display);
  }
  Object.values(appointment.requiredMap || {}).forEach((expert) => {
    // The requester is often a required participant as well, don't display them twice.
    if (expert.display && !names.includes(expert.display)) {
      names.push(expert.display);
    }
  });
  return names.join(', ');
};
